import { Link } from "react-router-dom";

import { useRecentPresentations } from "@/api/presentation-review";
import type { RecentPresentationItem } from "@/api/types";
import { EmptyState } from "@/components/EmptyState";
import { ErrorBanner } from "@/components/ErrorBanner";
import { RelativeTime } from "@/components/RelativeTime";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { formatJobNumber } from "@/lib/format";

/**
 * `/presentations` (index) — the "Pending" subtab of the
 * presentation-review flow. Rendered inside {@link PresentationsLayout}.
 *
 * Reads the wider `/api/presentations/recent` feed (pending + short
 * decided tail) and shows only the `pending_review` rows here — the
 * decided sibling now lives on `/presentations/decided` (issue #606)
 * with proper filter + date-range UX. The "Recent decisions" preview
 * that used to sit under Pending was removed in the same change: one
 * click to Decided is fine and the split makes the pending view's
 * purpose obvious.
 *
 * Distinct from the `/review` surface: that page bundles pending
 * curator-draft batches with pending presentations for a
 * cross-milestone "what needs a decision now?" view; this page is
 * presentation-scoped.
 */
export function PresentationsPending() {
  const query = useRecentPresentations();

  if (query.isPending) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (query.isError) {
    return <ErrorBanner error={query.error} onRetry={() => void query.refetch()} />;
  }

  const pending = query.data.presentations.filter(
    (p) => p.status === "pending_review",
  );

  return (
    <div className="space-y-6">
      {pending.length > 0 && (
        <p className="text-sm text-text-muted">{pending.length} pending</p>
      )}

      {pending.length === 0 ? (
        <EmptyState
          title="Nothing awaiting review"
          description="No presentations are pending a decision right now."
        />
      ) : (
        <div className="space-y-3">
          {pending.map((p) => (
            <PresentationRow key={p.id} presentation={p} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Build the friendly row label from whichever identifier is available.
 * Preference order mirrors `PresentationReview`'s title fallback so a
 * row and its detail page carry the same visible name:
 *   1. `<client_name> — <client_company>`
 *   2. `Memo #00000001` (bigserial, always present — never nulled by
 *      anonymisation).
 * Never exposes a UUID as visible text. `shortId` remains available
 * on the row's `<a title=…>` for forensic hover, same posture as
 * `PresentationReview`.
 *
 * Exported for unit testing + reuse by the Decided subtab.
 */
export function buildRowTitle(
  row: Pick<RecentPresentationItem, "clientName" | "clientCompany" | "memoNumber">,
): string {
  if (row.clientName && row.clientCompany) {
    return `${row.clientName} — ${row.clientCompany}`;
  }
  return `Memo #${formatJobNumber(row.memoNumber)}`;
}

function PresentationRow({
  presentation,
}: {
  presentation: RecentPresentationItem;
}) {
  const title = buildRowTitle(presentation);
  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle>
              <Link
                to={`/presentations/${formatJobNumber(presentation.memoNumber)}/review`}
                className="underline-offset-2 hover:underline"
                title={presentation.shortId}
              >
                {title}
              </Link>
            </CardTitle>
            <CardDescription className="mt-1">
              <Badge variant="accent">pending_review</Badge>
              {presentation.solutionType && (
                <> · {presentation.solutionType}</>
              )}
              {presentation.reviewDeadlineAt && (
                <>
                  {" · Deadline "}
                  <RelativeTime date={presentation.reviewDeadlineAt} />
                </>
              )}
            </CardDescription>
          </div>
          <div className="flex flex-col items-end gap-1">
            <Badge
              variant={
                presentation.matchValidationStatus === "flagged"
                  ? "danger"
                  : "default"
              }
            >
              {presentation.matchValidationStatus}
            </Badge>
            {presentation.matchValidationScore !== null &&
              presentation.solutionType !== "custom_build" && (
                <span className="text-xs text-text-muted">
                  Score {Math.round(presentation.matchValidationScore * 100)}%
                </span>
              )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="text-xs text-text-muted">
        Created <RelativeTime date={presentation.createdAt} />
      </CardContent>
    </Card>
  );
}
